/**
 * Creates a concurrency-limited function that invokes `fn` up to
 * `concurrency` times in parallel.
 *
 * https://gist.github.com/jasonmacgowan/a66ac4f72c7b1d34150adf1aa57276c1
 *
 * @param {Function} fn The function to concur
 * @param {number} concurrency The number of times `fn` should run in parallel
 */
export function concur<T, Func extends (...args: any) => Promise<T>>(fn: Func, concurrency = 2) {
  const queue: {
    self: any;
    args: Parameters<Func>;
    resolve: (...args: any) => void;
    reject: (reason?: any) => void;
  }[] = [];
  let running = 0;

  async function process() {
    if (queue.length) {
      if (running < concurrency) {
        running++;
        const next = queue.shift();
        if (next) {
          const { self, args, resolve, reject } = next;
          try {
            const result = await fn.apply(self, args);
            resolve(result);
          } catch (err) {
            reject(err);
          } finally {
            running--;
            process();
          }
        }
      }
    }
  }

  function concurred(this: any, ...args: Parameters<Func>) {
    return new Promise((resolve, reject) => {
      queue.push({ self: this, args, resolve, reject });
      process();
    });
  }

  return concurred;
}
